/**
 * Transient editorial analysis.
 *
 * Input: post fields + TipTap-derived body (HTML with {photo|igroup:uuid} tokens).
 * Output: a render plan — not persisted — consumed by compositions.
 */

import { splitBodyForRender } from "../utils/postContent";
import { normalizeComposition } from "./compositions";

function classifyImageRole(_index) {
  return "inline";
}

function normalizeAuthor(post) {
  if (post?.author?.name) return post.author;
  if (post?.author_name) return { name: post.author_name };
  return null;
}

function normalizeTags(post) {
  if (Array.isArray(post?.tags)) return post.tags.filter(Boolean);
  if (typeof post?.tags === "string") {
    return post.tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }
  return [];
}

function resolveFocus(post, photoMap, photos) {
  const focusId = (
    post?.focus_image_id ||
    post?.focusImageId ||
    photos.find((p) => p.is_focus)?.media_uuid ||
    null
  );
  if (focusId) {
    const key = String(focusId).toLowerCase();
    return photoMap[key] || { media_uuid: focusId, public_url: `/media/${focusId}` };
  }
  return null;
}

/**
 * Build ordered content blocks from stored body + photo/igroup metadata.
 */
export function analyzeContentBlocks(
  body,
  photoMap,
  groupMap = {},
  tastingMap = {},
  { skipFocusId = null } = {}
) {
  const parts = splitBodyForRender(body);
  const blocks = [];
  let imageIndex = 0;

  for (const part of parts) {
    if (part.type === "html") {
      const html = String(part.value || "").trim();
      if (!html) continue;
      blocks.push({ type: "html", html });
      continue;
    }

    if (part.type === "igroup") {
      const group = groupMap[part.value] || {
        group_uuid: part.value,
        name: "Ingredients",
        items: [],
      };
      blocks.push({
        type: "igroup",
        groupUuid: part.value,
        group,
      });
      continue;
    }

    if (part.type === "tasting") {
      const tasting = tastingMap[part.value] || {
        tasting_uuid: part.value,
        title: "Smagning",
        payload: {},
      };
      blocks.push({
        type: "tasting",
        tastingUuid: part.value,
        tasting,
      });
      continue;
    }

    const id = part.value;
    if (skipFocusId && String(skipFocusId).toLowerCase() === id) {
      continue;
    }

    const photo = photoMap[id] || {};
    blocks.push({
      type: "image",
      mediaUuid: id,
      src: photo.public_url || `/media/${id}`,
      description: photo.description || "",
      role: classifyImageRole(imageIndex),
      isFocus: Boolean(photo.is_focus),
    });
    imageIndex += 1;
  }

  return blocks;
}

function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * @param {object} post
 */
export function createEditorialPlan(post) {
  const photos = post?.photos || [];
  const photoMap = Object.fromEntries(
    photos.map((p) => [String(p.media_uuid).toLowerCase(), p])
  );
  const groups = post?.ingredient_groups || [];
  const groupMap = Object.fromEntries(
    groups.map((g) => [String(g.group_uuid).toLowerCase(), g])
  );
  const tastings = post?.tastings || [];
  const tastingMap = Object.fromEntries(
    tastings.map((t) => [String(t.tasting_uuid).toLowerCase(), t])
  );
  const composition = normalizeComposition(post?.composition, {
    postType: post?.post_type,
  });
  const focus = resolveFocus(post, photoMap, photos);
  const focusId = focus?.media_uuid ? String(focus.media_uuid).toLowerCase() : null;

  const skipFocusInBody = composition === "feature" && focusId;
  const blocks = analyzeContentBlocks(post?.body || "", photoMap, groupMap, tastingMap, {
    skipFocusId: skipFocusInBody ? focusId : null,
  });

  return {
    composition,
    title: post?.title || "",
    author: normalizeAuthor(post),
    tags: normalizeTags(post),
    dateLabel: formatDate(post?.created_at) || formatDate(post?.published_at),
    visibility: post?.visibility || "private",
    isPrivate: (post?.visibility || "private") === "private",
    focusImageId: focusId,
    focus,
    photos,
    photoMap,
    ingredientGroups: groups,
    groupMap,
    tastings,
    tastingMap,
    blocks,
    instructions: post?.instructions || "",
    yieldAmount: post?.yield_amount || "",
    cookTime: post?.cook_time || "",
  };
}
